import React, { ChangeEventHandler, FC, useRef } from 'react'

import { Checkbox } from '@Components/FormElements/UI/Checkbox'
import { ImageV2 } from '@Components/Image'
import { Link } from '@Components/Link'
import { CrossSellProduct } from '@Globals/CMS/ProductHelpers'
import { useTranslation } from '@Globals/Translations'

import { UpSellTooltip } from '../UpSellTooltip/UpSellTooltip'
import styles from './UpSellItem.module.scss'

type UpSellItemProps = {
  id?: string
  onChange?: ChangeEventHandler<HTMLInputElement>
  product?: CrossSellProduct
  selected?: boolean
  type?: string
}

export const UpSellItem: FC<UpSellItemProps> = ({
  id,
  onChange,
  product,
  selected,
  type,
}) => {
  const { t } = useTranslation()
  const checkboxRef = useRef<HTMLInputElement | null>(null)

  if (!product) {
    return null
  }

  const {
    id: productID,
    masterVariant,
    name,
    primaryAsset,
    url,
  } = product
  const componentID = id || productID
  const hasDiscount = !!masterVariant?.price?.discount
  const isFree = masterVariant?.price?.value === 0 || masterVariant?.price?.discount?.value === 0 || false
  const label = t('products:ALSO_ADD_PRODUCT_TO_CART', { arg1: name })
  const priceLabel = isFree ? t('header:FREE_LABEL') : masterVariant?.price?.format

  // @ts-ignore
  const upsellType = type || product?.masterVariant?.type?.value.key

  const linkWrap = (content: React.ReactNode) => (
    url
      ? (
        <Link href={url} onClick={e => e.stopPropagation()}>
          {content}
        </Link>
      )
      : content
  )

  return (
    <div key={componentID} className={styles.item} onClick={() => checkboxRef.current?.click()} title={name || ''}>

      <div className={styles.content}>
        {primaryAsset?.url && (
          <div className={styles.image}>
            <ImageV2 alt={name || ''} height={100} src={primaryAsset.url} width={100} />
          </div>
        )}

        <div className={styles.description}>
          <div className={styles.name}>
            {linkWrap(name)}

            {upsellType === 'engrave' && (
              <UpSellTooltip>
                {t('products:TOOL_TIP_ENGRAVING')}
              </UpSellTooltip>
            )}

            {upsellType === 'deboss' && (
              <UpSellTooltip>
                {t('products:TOOL_TIP_DEBOSS')}
              </UpSellTooltip>
            )}

            {upsellType === 'giftWrappings' && (
              <UpSellTooltip>
                {t('products:TOOL_TIP_GIFT_WRAPPING')}
              </UpSellTooltip>
            )}
          </div>
          <div className={styles.priceWrapper}>
            {!isFree && '+ '}
            {hasDiscount && <span className={styles.beforePrice}>{`${masterVariant?.price?.format}`}</span>}
            <span className={`${styles.price} ${hasDiscount ? styles.discount : ''}`}>
              {`${!isFree && hasDiscount ? masterVariant?.price?.discount?.format : priceLabel}`}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.checkboxWrapper}>
        <Checkbox
          aria-label={label}
          checked={selected}
          id={`crossSell${componentID}`}
          indicatorClassName={styles.checkbox}
          label=""
          onChange={onChange}
          ref={checkboxRef}
          title={label}
          value={componentID}
        />
      </div>
    </div>
  )
}
